"use client";

import Link from "next/link";
import { ArrowRight, CheckCircle2, Guitar, Shield, Zap } from "lucide-react";

const QUICK_POINTS = [
  "Kiểm tra 7 bước chuyên sâu",
  "Bảo hành 3 tháng rõ ràng",
  "Xem tận mặt, không mua qua ảnh",
];

/**
 * GuitarDaQuaSuDungCTA
 *
 * Drop this anywhere on the homepage (or any page).
 * Links to /guitar-da-qua-su-dung.
 * Dark mode, brand-consistent gold accent.
 */
export function GuitarDaQuaSuDungCTA() {
  return (
    <section
      className="w-full px-4 sm:px-6 lg:px-8 py-4"
      aria-label="Guitar đã qua sử dụng"
      style={{ fontFamily: "var(--font-roboto)" }}
    >
      <Link
        href="/guitar-da-qua-su-dung"
        className="group block max-w-7xl mx-auto rounded-3xl border overflow-hidden transition-all duration-300"
        style={{
          backgroundColor: "var(--card)",
          borderColor: "rgba(200,169,110,0.28)",
          textDecoration: "none",
        }}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.borderColor = "rgba(200,169,110,0.55)";
          el.style.boxShadow = "0 20px 56px rgba(0,0,0,0.22), 0 0 0 1px rgba(200,169,110,0.1)";
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLElement;
          el.style.borderColor = "rgba(200,169,110,0.28)";
          el.style.boxShadow = "none";
        }}
      >
        {/* Gold top bar */}
        <div className="h-1" style={{ background: "linear-gradient(to right, var(--brand-primary), rgba(200,169,110,0.4))" }} />

        <div className="p-6 sm:p-8 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center gap-8">

          {/* Left: icon + text */}
          <div className="flex items-start gap-5 flex-1 min-w-0">
            {/* Icon box */}
            <div
              className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center border transition-all duration-300 group-hover:scale-105"
              style={{
                backgroundColor: "rgba(200,169,110,0.1)",
                borderColor: "rgba(200,169,110,0.3)",
              }}
            >
              <Guitar className="w-7 h-7 sm:w-8 sm:h-8" style={{ color: "var(--brand-primary)" }} />
            </div>

            <div className="flex-1 min-w-0">
              {/* Badge */}
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border mb-3"
                style={{
                  backgroundColor: "rgba(200,169,110,0.08)",
                  borderColor: "rgba(200,169,110,0.3)",
                  color: "var(--brand-primary)",
                  fontFamily: "var(--font-montserrat)",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                <Shield className="w-3 h-3" />
                Đã kiểm tra · Bảo hành
              </span>

              <h2
                className="text-xl sm:text-2xl font-black leading-tight mb-2"
                style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
              >
                Guitar Đã Qua Sử Dụng{" "}
                <span style={{ color: "var(--brand-primary)" }}>Chất Lượng</span>
              </h2>

              <p className="text-sm sm:text-base mb-4" style={{ color: "var(--muted-foreground)" }}>
                Acoustic · Classic · Điện — mỗi cây đàn đều qua kiểm tra kỹ, giá minh bạch,{" "}
                <strong style={{ color: "var(--foreground)" }}>tiết kiệm 40–60%</strong> so với mua mới.
              </p>

              {/* Quick points */}
              <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                {QUICK_POINTS.map((pt) => (
                  <div key={pt} className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--brand-primary)" }} />
                    <span className="text-xs font-semibold" style={{ color: "var(--muted-foreground)" }}>{pt}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: CTA button */}
          <div className="flex-shrink-0 w-full lg:w-auto">
            <div
              className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-xl font-black text-sm transition-all duration-200 group-hover:opacity-90 active:scale-[0.97] w-full lg:w-auto justify-center"
              style={{
                fontFamily: "var(--font-montserrat)",
                backgroundColor: "var(--brand-primary)",
                color: "var(--brand-primary-foreground)",
                boxShadow: "0 4px 20px rgba(200,169,110,0.28)",
              }}
            >
              <Zap className="w-4 h-4" />
              Khám phá ngay
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </div>

            <p
              className="text-xs text-center mt-2"
              style={{ color: "var(--muted-foreground)" }}
            >
              Đang có <strong style={{ color: "var(--brand-primary)" }}>500+</strong> cây đàn
            </p>
          </div>

        </div>
      </Link>
    </section>
  );
}